import { useToast } from '../../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../../utils/apiError';
import type {
    usePreviewOrthopulseOrderWithShipTo,
    useSendOrthopulseOrder
} from '../../../../../hooks/useOrthopulseOrder';
import { buildShipToPayload } from './shipToTypes';
import type { useShipToForms } from './useShipToForms';
import type { useForceOverrideState } from './useForceOverrideState';
import type { OrthopulseOrderStep } from './step';

interface UseOrthopulseOrderActionsParams {
    shipToForms: ReturnType<typeof useShipToForms>;
    isValid: boolean;
    forceOverride: ReturnType<typeof useForceOverrideState>;
    previewWithShipTo: ReturnType<typeof usePreviewOrthopulseOrderWithShipTo>;
    sendOrder: ReturnType<typeof useSendOrthopulseOrder>;
    setStep: (step: OrthopulseOrderStep) => void;
    onDataChange?: () => void;
}

interface UseOrthopulseOrderActionsResult {
    handlePreview: () => void;
    handleSend: () => void;
    isPreviewDisabled: boolean;
}

// Owns the two POSTs (preview-with-shipTo, send) so useOrthopulseOrderFlow
// stays a thin composition of state hooks.
export function useOrthopulseOrderActions({
    shipToForms,
    isValid,
    forceOverride,
    previewWithShipTo,
    sendOrder,
    setStep,
    onDataChange
}: UseOrthopulseOrderActionsParams): UseOrthopulseOrderActionsResult {
    const { showToast } = useToast();

    const handlePreview = async () => {
        try {
            const shipTo = buildShipToPayload(shipToForms.target, shipToForms.values);
            await previewWithShipTo.mutateAsync({
                shipTo,
                forceOverride: forceOverride.buildPayload()
            });
            setStep('preview');
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to load the order preview'), 'error');
        }
    };

    const handleSend = async () => {
        try {
            const shipTo = buildShipToPayload(shipToForms.target, shipToForms.values);
            const result = await sendOrder.mutateAsync({
                shipTo,
                forceOverride: forceOverride.buildPayload()
            });
            onDataChange?.();
            showToast(`OrthoPulse order sent (PO ${result.poNumber}).`, 'success');
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to send OrthoPulse order'), 'error');
        }
    };

    const isPreviewDisabled =
        !isValid ||
        previewWithShipTo.isPending ||
        (forceOverride.open && !!forceOverride.reasonError);

    return {
        handlePreview: () => void handlePreview(),
        handleSend: () => void handleSend(),
        isPreviewDisabled
    };
}
